import React from 'react';
import API from '../../../api/api';
import MediaAPI, { MediaLibraryItem, MediaLibraryListQuery, MediaStats } from '../../../api/media';
import { AssetPoolItem } from '../assetPool';
import {
  BULK_DESCRIBE_THRESHOLD,
  countItemsByFolder,
  filterLibraryItems,
  FolderType,
  inferUploadType,
  mergeLibraryItems,
  readMediaAiDescription,
  readMediaContextDraft,
  SourceFilter,
  toActionableError,
} from './helpers';
import { useSelection } from './useSelection';
import {
  MEDIA_LIBRARY_INVALIDATE_EVENT,
  MediaLibraryInvalidateDetail,
} from '../../../hooks/mediaLibraryEvents';
import {
  buildMediaLibraryUrlSearch,
  BUILT_IN_MEDIA_PRESETS,
  createdAfterForRecentDays,
  DEFAULT_MEDIA_LIBRARY_QUERY,
  LibraryViewMode,
  makePresetId,
  MediaFilterPreset,
  MediaFilterPresetValues,
  MediaLibraryQueryState,
  MediaPageSize,
  persistSavedMediaPresets,
  readMediaLibraryUrlState,
  readSavedMediaPresets,
} from './mediaLibraryQuery';
import { SortKey } from './mediaCardUtils';

export type AttachmentLibraryMode = 'manage' | 'select';
export type AttachmentLibraryTab = 'upload' | 'browse';
export type { LibraryViewMode } from './mediaLibraryQuery';

const PERSISTENCE_POLL_MS = 2500;
const PERSISTENCE_POLL_MAX_MS = 2 * 60 * 1000;

// ─── Module-level page cache (dependency-free "keepPreviousData") ──────────────
// Keyed by the query signature so reopening the modal (or revisiting a page/search)
// paints the last results instantly while a background revalidation runs. The cache
// is intentionally module-scoped so it survives modal close/unmount without a
// QueryClientProvider. Bounded so it can't grow without limit.
interface CachedPage {
  items: MediaLibraryItem[];
  total: number;
  page: number;
}

const MAX_CACHE_ENTRIES = 32;
const libraryPageCache = new Map<string, CachedPage>();
let libraryCacheGeneration = 0;

// Server-side filter signature carried alongside page/search. The type and
// source filters now reach the backend (the grid is a true server-paged,
// server-filtered list), so a cached page is only valid for the SAME filter
// combination — otherwise switching from "all" to "video" could paint the
// cached all-types page. Keyed defensively (default 'all') for back-compat.
interface LibraryFilters {
  type?: FolderType;
  source?: SourceFilter;
  sortKey?: SortKey;
  pageSize?: MediaPageSize;
  viewMode?: LibraryViewMode;
  hasControlnet?: boolean;
  recentDays?: number | null;
}

const filterSignature = (filters?: LibraryFilters): string =>
  [
    filters?.type ?? 'all',
    filters?.source ?? 'all',
    filters?.sortKey ?? 'newest',
    filters?.pageSize ?? 20,
    filters?.viewMode ?? 'all',
    filters?.hasControlnet ? 'controlnet' : 'any-control',
    filters?.recentDays ?? 'all-time',
  ].join('|');

const pageCacheKey = (page: number, search: string, filters?: LibraryFilters): string =>
  `${page}::${search.trim().toLowerCase()}::${filterSignature(filters)}`;

const readPageCache = (
  page: number,
  search: string,
  filters?: LibraryFilters
): CachedPage | undefined => libraryPageCache.get(pageCacheKey(page, search, filters));

const writePageCache = (
  page: number,
  search: string,
  value: CachedPage,
  filters?: LibraryFilters
): void => {
  const key = pageCacheKey(page, search, filters);
  // Refresh insertion order so the LRU eviction below keeps recent pages.
  libraryPageCache.delete(key);
  libraryPageCache.set(key, value);
  while (libraryPageCache.size > MAX_CACHE_ENTRIES) {
    const oldest = libraryPageCache.keys().next().value;
    if (oldest === undefined) break;
    libraryPageCache.delete(oldest);
  }
};

// Patch a single item's favourite flag across every cached page so a background
// revalidation that predates an optimistic toggle can't resurrect a stale flag.
const patchCachedFavourite = (mediaId: string, isFavourite: boolean): void => {
  libraryPageCache.forEach((entry) => {
    entry.items = entry.items.map((item) =>
      (item.media_id || item.id) === mediaId ? { ...item, is_favourite: isFavourite } : item
    );
  });
};

// Drop the whole cache after a list-mutating action (upload/rename/delete/metadata)
// so a subsequent reopen revalidates instead of painting pre-mutation rows.
const invalidateLibraryCache = (): void => {
  libraryPageCache.clear();
  libraryCacheGeneration += 1;
};

// Test-only: reset module cache between renders.
export const __resetLibraryPageCacheForTests = (): void => {
  libraryPageCache.clear();
  libraryCacheGeneration += 1;
};

interface UseAttachmentLibraryStateProps {
  isOpen: boolean;
  mode: AttachmentLibraryMode;
  generatedAssets: AssetPoolItem[];
  initialSelectedMediaIds: string[];
  /**
   * Full item objects to seed the cross-page selection with. Preferred over
   * `initialSelectedMediaIds` because the objects survive even if their page is
   * never re-visited (an ids-only seed can only resolve to an object once the
   * page that contains it loads). Callers that already hold the picked
   * references (e.g. NewDatasetRunModal) pass them here.
   */
  initialSelectedItems?: MediaLibraryItem[];
  /** Keep the standalone page query in browser history; modals remain isolated. */
  syncUrl?: boolean;
}

const mediaKey = (item: { media_id?: string; id?: string }): string =>
  item.media_id || item.id || '';

export const useAttachmentLibraryState = ({
  isOpen,
  mode,
  generatedAssets,
  initialSelectedMediaIds,
  initialSelectedItems,
  syncUrl = false,
}: UseAttachmentLibraryStateProps) => {
  const initialQueryRef = React.useRef<MediaLibraryQueryState>(
    syncUrl && typeof window !== 'undefined'
      ? readMediaLibraryUrlState(window.location.search)
      : DEFAULT_MEDIA_LIBRARY_QUERY
  );
  const initialQuery = initialQueryRef.current;
  const wasOpenRef = React.useRef(false);
  const lastModeRef = React.useRef<AttachmentLibraryMode>(mode);
  // Read the latest seed objects via a ref so the open effect can consume them
  // without listing `initialSelectedItems` in its deps — callers typically pass
  // a freshly-mapped array each render, and depending on it would re-fire the
  // open effect on every parent render (wiping the in-progress selection).
  const initialSelectedItemsRef = React.useRef<MediaLibraryItem[]>(initialSelectedItems ?? []);
  initialSelectedItemsRef.current = initialSelectedItems ?? [];
  const [activeTab, setActiveTab] = React.useState<AttachmentLibraryTab>('browse');
  const [folderType, setFolderType] = React.useState<FolderType>(initialQuery.folderType);
  const [sourceFilter, setSourceFilter] = React.useState<SourceFilter>(initialQuery.sourceFilter);
  const [searchQuery, setSearchQuery] = React.useState(initialQuery.search);
  const [sortKey, setSortKey] = React.useState<SortKey>(initialQuery.sortKey);
  const [pageSize, setPageSize] = React.useState<MediaPageSize>(initialQuery.pageSize);
  const [hasControlnet, setHasControlnet] = React.useState(initialQuery.hasControlnet);
  const [recentDays, setRecentDays] = React.useState<number | null>(initialQuery.recentDays);
  const [activePresetId, setActivePresetId] = React.useState<string | null>(initialQuery.activePresetId);
  const [savedPresets, setSavedPresets] = React.useState<MediaFilterPreset[]>(readSavedMediaPresets);
  const [libraryItems, setLibraryItems] = React.useState<MediaLibraryItem[]>([]);
  const [dismissedMediaIds, setDismissedMediaIds] = React.useState<string[]>([]);
  // Cross-page selection model (page-independent `Map<id, item>`). Persists the
  // FULL picked objects across page loads/searches so picks made on page 1
  // survive a navigation to page 2 (feeding the "Add Selected (N)" count +
  // confirm payload). Extracted into `useSelection` so the same model can power
  // any picker; `selection.ids` is the ordered key list BrowseTab reads to render
  // a checkmark on whatever page a pick appears.
  const selection = useSelection('multi');
  const selectedIds = selection.ids;
  // Stable refs to selection mutators so effects/callbacks can invoke them
  // without taking the (per-render) `selection` object as a dependency — keeps
  // the open effect and memoized row callbacks from re-firing on every toggle.
  const seedSelectionRef = React.useRef(selection.seed);
  seedSelectionRef.current = selection.seed;
  const mergeSelectionRef = React.useRef(selection.mergeLoadedItems);
  mergeSelectionRef.current = selection.mergeLoadedItems;
  const toggleSelectionRef = React.useRef(selection.toggle);
  toggleSelectionRef.current = selection.toggle;
  const addManySelectionRef = React.useRef(selection.addMany);
  addManySelectionRef.current = selection.addMany;
  const removeSelectionRef = React.useRef(selection.remove);
  removeSelectionRef.current = selection.remove;
  const [activeMediaId, setActiveMediaId] = React.useState('');
  // Always-current ref to the active media id, so an in-flight async handler
  // (describe) can tell — at *resolution* time — whether the user has since
  // navigated to a different item, without depending on `activeMediaId` (which
  // would make the handler itself stale-closured over the id it started with).
  const activeMediaIdRef = React.useRef(activeMediaId);
  activeMediaIdRef.current = activeMediaId;
  const [contextDraft, setContextDraft] = React.useState('');
  const [descriptionDraft, setDescriptionDraft] = React.useState('');
  const [renameDraft, setRenameDraft] = React.useState('');
  const [uploadFiles, setUploadFiles] = React.useState<File[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const [savingContext, setSavingContext] = React.useState(false);
  const [describing, setDescribing] = React.useState(false);
  const [savingDescription, setSavingDescription] = React.useState(false);
  const [renaming, setRenaming] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  const [statusMessage, setStatusMessage] = React.useState('');
  const [errorMessage, setErrorMessage] = React.useState('');

  // Pagination state
  const [currentPage, setCurrentPage] = React.useState(initialQuery.page);
  const [totalItems, setTotalItems] = React.useState(0);
  const [folderTotals, setFolderTotals] = React.useState<Record<FolderType, number>>({ all: 0, image: 0, video: 0, audio: 0 });
  const [apiStats, setApiStats] = React.useState<MediaStats | null>(null);
  const [fullLibraryItems, setFullLibraryItems] = React.useState<MediaLibraryItem[]>([]);

  // View mode and favourite state
  const [viewMode, setViewMode] = React.useState<LibraryViewMode>(initialQuery.viewMode);
  const [selectingAll, setSelectingAll] = React.useState(false);
  const [allMatchingSelected, setAllMatchingSelected] = React.useState(false);
  const selectionOperationVersionRef = React.useRef(0);
  const [togglingFavouriteId, setTogglingFavouriteId] = React.useState<string | null>(null);

  // Favourite ids with an in-flight optimistic toggle, mapped to their optimistic
  // value. A background revalidation must not clobber these (the toggle wins).
  const inflightFavouritesRef = React.useRef<Map<string, boolean>>(new Map());

  // Tracks the signature of the most recent successful fetch so we can skip a
  // redundant refetch when nothing changed since the modal last closed.
  const lastLoadedKeyRef = React.useRef<string | null>(null);

  // Signature of the request currently in flight, to collapse duplicate concurrent
  // loads (e.g. the open effect + BrowseTab's mount-time debounced search both
  // request page 1 / empty search) into a single network round-trip.
  const inflightLoadKeyRef = React.useRef<string | null>(null);
  const inflightPrefetchKeysRef = React.useRef<Set<string>>(new Set());
  // Monotonic request token: when navigation, filters, or a WebSocket refresh
  // start a newer request, an older response may populate its cache but must
  // never overwrite the visible page.
  const loadRequestVersionRef = React.useRef(0);

  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  // Reconcile a freshly-fetched list against any in-flight optimistic favourites
  // so a stale server response can't revert a toggle that is still being saved.
  const reconcileInflightFavourites = React.useCallback((list: MediaLibraryItem[]): MediaLibraryItem[] => {
    if (inflightFavouritesRef.current.size === 0) return list;
    return list.map((item) => {
      const id = item.media_id || item.id;
      const pending = inflightFavouritesRef.current.get(id);
      return pending === undefined ? item : { ...item, is_favourite: pending };
    });
  }, []);

  // Current server-side filter signature, read via refs so `loadLibrary` stays a
  // stable callback (it doesn't re-create when a filter toggles) while still
  // sending the active filters. The grid is now a true server-paged/-filtered
  // list, so the type/source filters must reach the API, not just trim the page.
  const folderTypeRef = React.useRef<FolderType>(folderType);
  folderTypeRef.current = folderType;
  const sourceFilterRef = React.useRef<SourceFilter>(sourceFilter);
  sourceFilterRef.current = sourceFilter;
  const sortKeyRef = React.useRef<SortKey>(sortKey);
  sortKeyRef.current = sortKey;
  const pageSizeRef = React.useRef<MediaPageSize>(pageSize);
  pageSizeRef.current = pageSize;
  const viewModeRef = React.useRef<LibraryViewMode>(viewMode);
  viewModeRef.current = viewMode;
  const hasControlnetRef = React.useRef(hasControlnet);
  hasControlnetRef.current = hasControlnet;
  const recentDaysRef = React.useRef<number | null>(recentDays);
  recentDaysRef.current = recentDays;
  const searchQueryRef = React.useRef(searchQuery);
  searchQueryRef.current = searchQuery;
  const currentPageRef = React.useRef(currentPage);
  currentPageRef.current = currentPage;

  const loadLibrary = React.useCallback(async (
    pageArg?: number,
    searchArg?: string,
    filtersArg?: LibraryFilters,
    force = false
  ) => {
    const page = pageArg ?? currentPageRef.current;
    const search = searchArg ?? searchQueryRef.current;
    // Resolve the active filters (explicit arg wins; otherwise read the live
    // ref). 'all' means "no filter" and is omitted from the request/cache key.
    const rawType = filtersArg?.type ?? folderTypeRef.current;
    const rawSource = filtersArg?.source ?? sourceFilterRef.current;
    const rawSortKey = filtersArg?.sortKey ?? sortKeyRef.current;
    const rawPageSize = filtersArg?.pageSize ?? pageSizeRef.current;
    const rawViewMode = filtersArg?.viewMode ?? viewModeRef.current;
    const rawHasControlnet = filtersArg?.hasControlnet ?? hasControlnetRef.current;
    const rawRecentDays = filtersArg && 'recentDays' in filtersArg
      ? filtersArg.recentDays ?? null
      : recentDaysRef.current;
    const typeParam = rawType && rawType !== 'all' ? rawType : undefined;
    const effectiveSource = rawViewMode === 'uploads' ? 'uploaded' : rawSource;
    const sourceParam = effectiveSource && effectiveSource !== 'all' ? effectiveSource : undefined;
    const filters: LibraryFilters = {
      type: rawType,
      source: rawSource,
      sortKey: rawSortKey,
      pageSize: rawPageSize,
      viewMode: rawViewMode,
      hasControlnet: rawHasControlnet,
      recentDays: rawRecentDays,
    };
    const requestQuery: MediaLibraryListQuery = {
      page,
      limit: rawPageSize,
      search: search || undefined,
      type: typeParam,
      source: sourceParam,
      favourite: rawViewMode === 'favourites' ? true : undefined,
      sort: rawSortKey,
      created_after: createdAfterForRecentDays(rawRecentDays),
      has_controlnet: rawHasControlnet ? true : undefined,
    };

    const key = pageCacheKey(page, search, filters);
    const requestCacheGeneration = libraryCacheGeneration;
    // Collapse duplicate concurrent loads for the same signature.
    if (!force && inflightLoadKeyRef.current === key) return;
    const requestVersion = ++loadRequestVersionRef.current;
    inflightLoadKeyRef.current = key;

    const cached = readPageCache(page, search, filters);
    if (cached) {
      // Instant paint from cache; revalidate in the background (no spinner, keep
      // showing the cached page while the network request is in flight).
      setLibraryItems(reconcileInflightFavourites(cached.items));
      setTotalItems(cached.total);
      setCurrentPage(cached.page);
    } else {
      // No cache for this signature — keep whatever is currently shown
      // (keepPreviousData) and surface a spinner only when there's nothing yet.
      setLoading(true);
    }
    setErrorMessage('');
    try {
      const result = await API.listMediaLibraryPaged(requestQuery);
      const items = Array.isArray(result.items) ? result.items : [];
      const total = result.total ?? items.length;
      const resolvedPage = result.page ?? page;
      if (requestCacheGeneration !== libraryCacheGeneration) return;
      writePageCache(page, search, { items, total, page: resolvedPage }, filters);
      if (requestVersion !== loadRequestVersionRef.current) return;
      lastLoadedKeyRef.current = pageCacheKey(page, search, filters);
      setLibraryItems(reconcileInflightFavourites(items));
      setTotalItems(total);
      setCurrentPage(resolvedPage);

      // Warm only the immediately adjacent pages. The cache is bounded and the
      // request is deliberately detached from visible loading/error state, so
      // a failed prefetch never disrupts the page the user is viewing.
      const pageCount = Math.max(1, Math.ceil(total / rawPageSize));
      const adjacentPages = [resolvedPage - 1, resolvedPage + 1].filter(
        (candidate) => candidate >= 1 && candidate <= pageCount
      );
      adjacentPages.forEach((candidate) => {
        const adjacentKey = pageCacheKey(candidate, search, filters);
        if (readPageCache(candidate, search, filters) || inflightPrefetchKeysRef.current.has(adjacentKey)) {
          return;
        }
        inflightPrefetchKeysRef.current.add(adjacentKey);
        const prefetchCacheGeneration = libraryCacheGeneration;
        void API.listMediaLibraryPaged({ ...requestQuery, page: candidate })
          .then((adjacentResult) => {
            if (prefetchCacheGeneration !== libraryCacheGeneration) return;
            writePageCache(
              candidate,
              search,
              {
                items: Array.isArray(adjacentResult.items) ? adjacentResult.items : [],
                total: adjacentResult.total ?? total,
                page: adjacentResult.page ?? candidate,
              },
              filters
            );
          })
          .catch(() => {})
          .finally(() => inflightPrefetchKeysRef.current.delete(adjacentKey));
      });

      // Facet counts are library-wide (NOT derived from the filtered page), so a
      // type/source absent from the current page never reads as "0". Refresh on
      // the first page load and whenever the cached stats are still empty.
      if (page === 1) {
        MediaAPI.getMediaStats().then(stats => {
          setApiStats(stats);
          setFolderTotals({ all: stats.total, image: stats.image, video: stats.video, audio: stats.audio });
        }).catch(() => {});
      }
    } catch (error) {
      if (requestVersion !== loadRequestVersionRef.current) return;
      setErrorMessage(toActionableError(error, 'list'));
      // On a cache miss there is nothing to fall back to; clear. On a cache hit
      // we keep the previously-painted results rather than blanking the grid.
      if (!cached) {
        setLibraryItems([]);
        setTotalItems(0);
      }
    } finally {
      if (requestVersion === loadRequestVersionRef.current && inflightLoadKeyRef.current === key) {
        inflightLoadKeyRef.current = null;
        setLoading(false);
      }
    }
  }, [reconcileInflightFavourites]);

  React.useEffect(() => {
    const justOpened = isOpen && !wasOpenRef.current;
    const modeChangedWhileOpen = isOpen && lastModeRef.current !== mode;
    if (!isOpen) {
      wasOpenRef.current = false;
      lastModeRef.current = mode;
      return;
    }
    if (!justOpened && !modeChangedWhileOpen) return;

    setActiveTab(mode === 'manage' ? 'upload' : 'browse');
    const openQuery = syncUrl ? initialQueryRef.current : DEFAULT_MEDIA_LIBRARY_QUERY;
    if (!syncUrl) {
      setFolderType(openQuery.folderType);
      setSourceFilter(openQuery.sourceFilter);
      setSearchQuery(openQuery.search);
      setSortKey(openQuery.sortKey);
      setPageSize(openQuery.pageSize);
      setViewMode(openQuery.viewMode);
      setHasControlnet(openQuery.hasControlnet);
      setRecentDays(openQuery.recentDays);
      setActivePresetId(null);
    }
    selectionOperationVersionRef.current += 1;
    setAllMatchingSelected(false);
    // Seed the cross-page selection on open. Prefer the full objects in
    // `initialSelectedItems` (they survive even if their page never loads);
    // fall back to ids-only callers, whose objects resolve when their page
    // appears (see the merge effect below).
    const seedItems = initialSelectedItemsRef.current;
    seedSelectionRef.current(seedItems, initialSelectedMediaIds);
    const seedIds =
      seedItems.length > 0 ? seedItems.map(mediaKey).filter(Boolean) : initialSelectedMediaIds;
    // Only focus a seeded item in the detail inspector in manage mode. In select
    // mode the sidebar is selection-driven, not detail-driven, so focusing a
    // picked item there would just duplicate its name in the panel.
    setActiveMediaId(mode === 'manage' ? seedIds[0] || '' : '');
    setContextDraft('');
    setDescriptionDraft('');
    setRenameDraft('');
    setUploadFiles([]);
    setDismissedMediaIds([]);
    setStatusMessage('');
    setErrorMessage('');
    setFullLibraryItems([]);

    // Paint the last page-1 results instantly from the module cache instead of
    // clearing to an empty spinner on every open.
    const openFilters: LibraryFilters = {
      type: openQuery.folderType,
      source: openQuery.sourceFilter,
      sortKey: openQuery.sortKey,
      pageSize: openQuery.pageSize,
      viewMode: openQuery.viewMode,
      hasControlnet: openQuery.hasControlnet,
      recentDays: openQuery.recentDays,
    };
    const cachedOpenPage = readPageCache(openQuery.page, openQuery.search, openFilters);
    if (cachedOpenPage) {
      setLibraryItems(reconcileInflightFavourites(cachedOpenPage.items));
      setTotalItems(cachedOpenPage.total);
      setCurrentPage(cachedOpenPage.page);
    } else {
      setLibraryItems([]);
      setCurrentPage(openQuery.page);
      setTotalItems(0);
      setFolderTotals({ all: 0, image: 0, video: 0, audio: 0 });
      setApiStats(null);
    }

    // Skip the network round-trip entirely when nothing changed since the modal
    // last closed (page 1 / empty search already loaded this session). The cached
    // paint above is already current. A reopen after a mode switch still revalidates.
    const firstPageKey = pageCacheKey(openQuery.page, openQuery.search, openFilters);
    const alreadyCurrent =
      justOpened && !modeChangedWhileOpen && lastLoadedKeyRef.current === firstPageKey && !!cachedOpenPage;
    if (!alreadyCurrent) {
      void loadLibrary(openQuery.page, openQuery.search, openFilters);
    }
    wasOpenRef.current = true;
    lastModeRef.current = mode;
  }, [initialSelectedMediaIds, isOpen, loadLibrary, mode, reconcileInflightFavourites, syncUrl]);

  // The standalone page owns URL history; modal instances opt out so opening a
  // picker cannot rewrite the parent page's address. The first render replaces
  // invalid/default noise, while subsequent query changes create real Back
  // button entries.
  const urlInitializedRef = React.useRef(false);
  const suppressNextUrlWriteRef = React.useRef(false);
  React.useEffect(() => {
    if (!syncUrl || !isOpen || typeof window === 'undefined') return;
    const nextSearch = buildMediaLibraryUrlSearch({
      page: currentPage,
      pageSize,
      search: searchQuery,
      folderType,
      sourceFilter,
      sortKey,
      viewMode,
      hasControlnet,
      recentDays,
      activePresetId,
    });
    const nextUrl = `${window.location.pathname}${nextSearch}${window.location.hash}`;
    if (!urlInitializedRef.current) {
      urlInitializedRef.current = true;
      if (window.location.search !== nextSearch) window.history.replaceState(null, '', nextUrl);
      return;
    }
    if (suppressNextUrlWriteRef.current) {
      suppressNextUrlWriteRef.current = false;
      return;
    }
    if (window.location.search !== nextSearch) window.history.pushState(null, '', nextUrl);
  }, [
    activePresetId,
    currentPage,
    folderType,
    hasControlnet,
    isOpen,
    pageSize,
    recentDays,
    searchQuery,
    sortKey,
    sourceFilter,
    syncUrl,
    viewMode,
  ]);

  React.useEffect(() => {
    if (!syncUrl || !isOpen || typeof window === 'undefined') return;
    const restoreFromHistory = () => {
      const next = readMediaLibraryUrlState(window.location.search);
      initialQueryRef.current = next;
      suppressNextUrlWriteRef.current = true;
      setFolderType(next.folderType);
      setSourceFilter(next.sourceFilter);
      setSearchQuery(next.search);
      setSortKey(next.sortKey);
      setPageSize(next.pageSize);
      setViewMode(next.viewMode);
      setHasControlnet(next.hasControlnet);
      setRecentDays(next.recentDays);
      setActivePresetId(next.activePresetId);
      setAllMatchingSelected(false);
      setCurrentPage(next.page);
      void loadLibrary(next.page, next.search, {
        type: next.folderType,
        source: next.sourceFilter,
        sortKey: next.sortKey,
        pageSize: next.pageSize,
        viewMode: next.viewMode,
        hasControlnet: next.hasControlnet,
        recentDays: next.recentDays,
      });
    };
    window.addEventListener('popstate', restoreFromHistory);
    return () => window.removeEventListener('popstate', restoreFromHistory);
  }, [isOpen, loadLibrary, syncUrl]);

  // Generator-profile tests persist the DB row before their image bytes. Poll
  // only while the current page contains a generating sentinel, then stop as
  // soon as every row is ready or failed. loadLibrary always revalidates its
  // cache entry, so this cannot get stuck repainting a cached waiting row.
  const hasPendingPersistence = libraryItems.some((item) => item.status === 'generating');
  React.useEffect(() => {
    if (!isOpen || !hasPendingPersistence) return;
    const timer = window.setInterval(() => {
      void loadLibrary(currentPage, searchQuery);
    }, PERSISTENCE_POLL_MS);
    const stopTimer = window.setTimeout(() => window.clearInterval(timer), PERSISTENCE_POLL_MAX_MS);
    return () => {
      window.clearInterval(timer);
      window.clearTimeout(stopTimer);
    };
  }, [currentPage, hasPendingPersistence, isOpen, loadLibrary, searchQuery]);

  // The app-level WebSocket owns the single connection and emits this browser
  // event for every media mutation. Always revalidate page one so a newly
  // created row is discoverable even when the current page had no generating
  // placeholder. Existing filters/search remain active and therefore honest.
  React.useEffect(() => {
    if (!isOpen || typeof window === 'undefined') return;
    const refresh = (event: Event) => {
      const detail = (event as CustomEvent<MediaLibraryInvalidateDetail>).detail;
      const isNewItem = detail?.eventType === 'AddMediaItem';
      invalidateLibraryCache();
      if (isNewItem) setAllMatchingSelected(false);
      void loadLibrary(isNewItem ? 1 : currentPage, searchQuery, undefined, true);
      if (
        isNewItem &&
        (searchQuery.trim() || folderTypeRef.current !== 'all' || sourceFilterRef.current !== 'all')
      ) {
        setStatusMessage('New media was saved. Active filters may hide it.');
      }
    };
    window.addEventListener(MEDIA_LIBRARY_INVALIDATE_EVENT, refresh);
    return () => window.removeEventListener(MEDIA_LIBRARY_INVALIDATE_EVENT, refresh);
  }, [currentPage, isOpen, loadLibrary, searchQuery]);

  // Recover from a missed/disconnected WebSocket event when the user returns
  // to the tab. This is a cheap first-page revalidation, not a polling loop.
  React.useEffect(() => {
    if (!isOpen || typeof document === 'undefined') return;
    const refreshOnVisible = () => {
      if (document.visibilityState !== 'visible') return;
      invalidateLibraryCache();
      void loadLibrary(1, searchQuery, undefined, true);
    };
    document.addEventListener('visibilitychange', refreshOnVisible);
    return () => document.removeEventListener('visibilitychange', refreshOnVisible);
  }, [isOpen, loadLibrary, searchQuery]);

  const items = React.useMemo(
    () =>
      mergeLibraryItems(libraryItems, generatedAssets).filter(
        (item) => !dismissedMediaIds.includes(item.media_id || item.id)
      ),
    [dismissedMediaIds, generatedAssets, libraryItems]
  );

  // Full item set (all pages) merged with generated assets — used for grouped view
  const fullItems = React.useMemo(
    () => {
      const base = fullLibraryItems.length > 0 ? fullLibraryItems : libraryItems;
      return mergeLibraryItems(base, generatedAssets).filter(
        (item) => !dismissedMediaIds.includes(item.media_id || item.id)
      );
    },
    [fullLibraryItems, libraryItems, generatedAssets, dismissedMediaIds]
  );

  const activeItem = React.useMemo(
    () => fullItems.find((item) => (item.media_id || item.id) === activeMediaId) || null,
    [activeMediaId, fullItems]
  );

  const aiDescription = React.useMemo(() => readMediaAiDescription(activeItem), [activeItem]);

  React.useEffect(() => {
    setContextDraft(readMediaContextDraft(activeItem));
    setDescriptionDraft(readMediaAiDescription(activeItem).text);
    setRenameDraft(activeItem?.name || '');
  }, [activeItem]);

  // The grid is server-paged AND server-filtered: the persisted rows in
  // `libraryItems` already satisfy the active type/source filter (the store
  // applies the same buckets, incl. ai_video→video and the uploaded/generated
  // source split, and folds legacy "persisted" rows into "generated"). So those
  // rows pass through UNTOUCHED — re-running the client heuristic here would
  // wrongly hide e.g. a generated row whose source is "persisted" (which the
  // client `isGeneratedSource` reads as not-generated). Only the in-memory
  // `generatedAssets` (which the server never saw) are filtered client-side.
  const serverRowIds = React.useMemo(
    () => new Set(libraryItems.map((item) => item.media_id || item.id)),
    [libraryItems]
  );
  const filteredItems = React.useMemo(() => {
    const serverRows = items.filter((item) => serverRowIds.has(item.media_id || item.id));
    const clientRows = items.filter((item) => !serverRowIds.has(item.media_id || item.id));
    const filteredClientRows = filterLibraryItems(clientRows, folderType, sourceFilter, '');
    // Preserve the merged order: keep each item in `items` order, dropping only
    // the client rows the filter rejected.
    const keep = new Set([
      ...serverRows.map((item) => item.media_id || item.id),
      ...filteredClientRows.map((item) => item.media_id || item.id),
    ]);
    return items.filter((item) => keep.has(item.media_id || item.id));
  }, [folderType, items, serverRowIds, sourceFilter]);

  // Library-wide facet counts for the type chips. These come from the
  // whole-library media stats (folderTotals), NOT the currently-loaded page —
  // otherwise a type absent from the current page (e.g. all-image page 1) reads
  // as "0 videos" even when videos exist elsewhere in the library. Until the
  // stats arrive we fall back to the per-page tally so the chips aren't blank.
  const pageFolderCounts = React.useMemo(() => countItemsByFolder(items), [items]);
  const folderCounts = React.useMemo<Record<FolderType, number>>(() => {
    if (apiStats) {
      return {
        all: folderTotals.all,
        image: folderTotals.image,
        video: folderTotals.video,
        audio: folderTotals.audio,
      };
    }
    return pageFolderCounts;
  }, [apiStats, folderTotals, pageFolderCounts]);

  // The confirmed selection is the union of the persistent map's values across
  // every page visited this session — NOT a per-page resolve. Ordered by
  // `selectedIds` so the payload follows click/seed order. Any id still awaiting
  // its object (seeded ids-only, page not yet loaded) is skipped until the merge
  // effect below resolves it.
  const selectedItems = selection.selectedItems;

  // Whenever a page loads, merge any freshly-loaded items whose id is in the
  // selection set into the persistent map. This (a) resolves objects for ids
  // seeded from `initialSelectedMediaIds` once their page appears and
  // (b) refreshes the stored object (e.g. a favourite toggle) for revisited
  // pages — without ever dropping picks from pages that aren't currently loaded.
  // The merge mutator is read via a ref so this fires on page loads only.
  const selectedIdsRef = React.useRef(selectedIds);
  selectedIdsRef.current = selectedIds;
  const itemsForMergeRef = React.useRef(items);
  itemsForMergeRef.current = items;
  // Key the effect on a STABLE signature of the loaded ids, not on `items`
  // identity. `items` is rebuilt every render (mergeLibraryItems returns a fresh
  // array, and `generatedAssets` may default to a new `[]`), so depending on it
  // directly would re-fire the merge on every render and, paired with the
  // setById it performs, spin a render loop. The signature only changes when the
  // set of loaded rows actually changes (a real page/search/dismiss), which is
  // exactly when a merge is needed.
  const loadedIdsSignature = React.useMemo(
    () => items.map((item) => item.media_id || item.id).join('|'),
    [items]
  );
  React.useEffect(() => {
    mergeSelectionRef.current(itemsForMergeRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedIdsSignature]);

  const handleSearchSubmit = React.useCallback((query: string) => {
    setActivePresetId(null);
    setAllMatchingSelected(false);
    setCurrentPage(1);
    void loadLibrary(1, query);
  }, [loadLibrary]);

  const handlePageChange = React.useCallback((page: number) => {
    void loadLibrary(page, searchQuery);
  }, [loadLibrary, searchQuery]);

  // Changing the type/source filter is a NEW server query: reset to page 1 and
  // reload with the new filter so the grid (and the filtered total that drives
  // pagination) reflect the whole library, not just the already-loaded page.
  // The new filter is passed explicitly to loadLibrary so it isn't lost to the
  // ref not yet reflecting this render's state update.
  const handleFolderTypeChange = React.useCallback((next: FolderType) => {
    setFolderType(next);
    setActivePresetId(null);
    setAllMatchingSelected(false);
    setCurrentPage(1);
    void loadLibrary(1, searchQuery, { type: next, source: sourceFilterRef.current });
  }, [loadLibrary, searchQuery]);

  const handleSourceFilterChange = React.useCallback((next: SourceFilter) => {
    setSourceFilter(next);
    const nextViewMode = viewModeRef.current === 'uploads' ? 'all' : viewModeRef.current;
    if (nextViewMode !== viewModeRef.current) setViewMode(nextViewMode);
    setActivePresetId(null);
    setAllMatchingSelected(false);
    setCurrentPage(1);
    void loadLibrary(1, searchQuery, {
      type: folderTypeRef.current,
      source: next,
      viewMode: nextViewMode,
    });
  }, [loadLibrary, searchQuery]);

  const handleSortChange = React.useCallback((next: SortKey) => {
    setSortKey(next);
    setActivePresetId(null);
    setAllMatchingSelected(false);
    setCurrentPage(1);
    void loadLibrary(1, searchQuery, { sortKey: next });
  }, [loadLibrary, searchQuery]);

  const handlePageSizeChange = React.useCallback((next: MediaPageSize) => {
    setPageSize(next);
    setAllMatchingSelected(false);
    setCurrentPage(1);
    void loadLibrary(1, searchQuery, { pageSize: next });
  }, [loadLibrary, searchQuery]);

  const handleViewModeChange = React.useCallback((next: LibraryViewMode) => {
    setViewMode(next);
    setActivePresetId(null);
    setAllMatchingSelected(false);
    setCurrentPage(1);
    void loadLibrary(1, searchQuery, { viewMode: next });
  }, [loadLibrary, searchQuery]);

  const handleHasControlnetChange = React.useCallback((next: boolean) => {
    setHasControlnet(next);
    setActivePresetId(null);
    setAllMatchingSelected(false);
    setCurrentPage(1);
    void loadLibrary(1, searchQuery, { hasControlnet: next });
  }, [loadLibrary, searchQuery]);

  const handleRecentDaysChange = React.useCallback((next: number | null) => {
    setRecentDays(next);
    setActivePresetId(null);
    setAllMatchingSelected(false);
    setCurrentPage(1);
    void loadLibrary(1, searchQuery, { recentDays: next });
  }, [loadLibrary, searchQuery]);

  const handleApplyPreset = React.useCallback((presetId: string) => {
    const preset = [...BUILT_IN_MEDIA_PRESETS, ...savedPresets].find((entry) => entry.id === presetId);
    if (!preset) return;
    const values = preset.values;
    setFolderType(values.folderType);
    setSourceFilter(values.sourceFilter);
    setSearchQuery(values.search);
    setSortKey(values.sortKey);
    setViewMode(values.viewMode);
    setHasControlnet(values.hasControlnet);
    setRecentDays(values.recentDays);
    setActivePresetId(preset.id);
    setAllMatchingSelected(false);
    setCurrentPage(1);
    void loadLibrary(1, values.search, {
      type: values.folderType,
      source: values.sourceFilter,
      sortKey: values.sortKey,
      viewMode: values.viewMode,
      hasControlnet: values.hasControlnet,
      recentDays: values.recentDays,
    });
  }, [loadLibrary, savedPresets]);

  const handleSavePreset = React.useCallback((name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const values: MediaFilterPresetValues = {
      search: searchQuery,
      folderType,
      sourceFilter,
      sortKey,
      viewMode,
      hasControlnet,
      recentDays,
    };
    const preset: MediaFilterPreset = { id: makePresetId(trimmed), name: trimmed, values };
    setSavedPresets((previous) => {
      const next = [...previous, preset];
      persistSavedMediaPresets(next);
      return next;
    });
    setActivePresetId(preset.id);
    setStatusMessage(`Saved filter preset “${trimmed}”.`);
  }, [folderType, hasControlnet, recentDays, searchQuery, sortKey, sourceFilter, viewMode]);

  const handleDeletePreset = React.useCallback((presetId: string) => {
    setSavedPresets((previous) => {
      const next = previous.filter((preset) => preset.id !== presetId);
      persistSavedMediaPresets(next);
      return next;
    });
    setActivePresetId((current) => (current === presetId ? null : current));
  }, []);

  const handleClearFilters = React.useCallback(() => {
    setFolderType('all');
    setSourceFilter('all');
    setSearchQuery('');
    setViewMode('all');
    setHasControlnet(false);
    setRecentDays(null);
    setActivePresetId(null);
    setAllMatchingSelected(false);
    setCurrentPage(1);
    void loadLibrary(1, '', {
      type: 'all',
      source: 'all',
      viewMode: 'all',
      hasControlnet: false,
      recentDays: null,
    });
  }, [loadLibrary]);

  const handleToggleFavourite = React.useCallback(async (mediaId: string, current: boolean) => {
    const next = !current;
    // Optimistic update first so the star turns gold immediately. Register the
    // pending value so any in-flight background revalidation reconciles to it
    // (the toggle wins the race against a stale refetch) and patch every cached
    // page so a later cache paint can't resurrect the old flag.
    inflightFavouritesRef.current.set(mediaId, next);
    patchCachedFavourite(mediaId, next);
    setLibraryItems(prev =>
      prev.map(item =>
        (item.media_id || item.id) === mediaId
          ? { ...item, is_favourite: next }
          : item
      )
    );
    setTogglingFavouriteId(mediaId);
    try {
      await API.toggleFavourite(mediaId, next);
    } catch {
      // Roll back on failure — both local state and the cache.
      patchCachedFavourite(mediaId, current);
      setLibraryItems(prev =>
        prev.map(item =>
          (item.media_id || item.id) === mediaId
            ? { ...item, is_favourite: current }
            : item
        )
      );
    } finally {
      // Only clear the in-flight marker if no newer toggle superseded this one.
      if (inflightFavouritesRef.current.get(mediaId) === next) {
        inflightFavouritesRef.current.delete(mediaId);
      }
      setTogglingFavouriteId(prev => (prev === mediaId ? null : prev));
    }
  }, []);

  // Read latest items/mode via refs so handleFileClick can be a stable
  // useCallback — keeping the memoized rows from re-rendering on every search
  // keystroke or favourite toggle just because this handler changed identity.
  const itemsRef = React.useRef(items);
  itemsRef.current = items;
  const modeRef = React.useRef(mode);
  modeRef.current = mode;

  const handleFileClick = React.useCallback(async (mediaId: string) => {
    if (modeRef.current !== 'select') {
      // Manage mode: a click focuses the item in the detail inspector.
      setActiveMediaId(mediaId);
      // Fetch full item details from DB if not already in list.
      const existing = itemsRef.current.find((item) => (item.media_id || item.id) === mediaId);
      if (!existing) {
        try {
          const fetched = await API.getMediaItem(mediaId);
          if (fetched) {
            setLibraryItems((prev) => {
              if (prev.some((item) => (item.media_id || item.id) === mediaId)) return prev;
              return [fetched as MediaLibraryItem, ...prev];
            });
          }
        } catch {
          // ignore fetch errors — the item may still show partial info
        }
      }
      return;
    }
    selectionOperationVersionRef.current += 1;
    setAllMatchingSelected(false);
    const alreadySelected = selectedIdsRef.current.includes(mediaId);
    if (alreadySelected) {
      // Toggle off by id (the item may not be on the current page).
      removeSelectionRef.current(mediaId);
    } else {
      const item = itemsRef.current.find((entry) => mediaKey(entry) === mediaId);
      if (item) {
        toggleSelectionRef.current(item);
      }
    }
  }, []);

  // Explicit selection toggle (the grid checkbox). Works in BOTH modes: in
  // select mode it drives the picks; in manage mode it drives the bulk-action
  // selection (favourite/delete). Distinct from `handleFileClick`, whose
  // manage-mode behavior is to open the inspector, not select.
  const handleToggleSelect = React.useCallback((mediaId: string) => {
    selectionOperationVersionRef.current += 1;
    setAllMatchingSelected(false);
    const alreadySelected = selectedIdsRef.current.includes(mediaId);
    if (alreadySelected) {
      removeSelectionRef.current(mediaId);
      return;
    }
    const item = itemsForMergeRef.current.find((entry) => mediaKey(entry) === mediaId);
    if (item) toggleSelectionRef.current(item);
  }, []);

  // Range-extend selection (shift-click) — add every id in the range.
  const handleRangeSelect = React.useCallback((mediaIds: string[]) => {
    selectionOperationVersionRef.current += 1;
    setAllMatchingSelected(false);
    const current = new Set(selectedIdsRef.current);
    mediaIds.forEach((id) => {
      if (current.has(id)) return;
      const item = itemsForMergeRef.current.find((entry) => mediaKey(entry) === id);
      if (item) toggleSelectionRef.current(item);
    });
  }, []);

  // Manage-mode inspector focus by id (open with a non-empty id, close with '').
  const handleInspect = React.useCallback((mediaId: string) => {
    setActiveMediaId(mediaId);
  }, []);

  const clearSelectionRef = React.useRef(selection.clear);
  clearSelectionRef.current = selection.clear;
  const handleClearSelection = React.useCallback(() => {
    selectionOperationVersionRef.current += 1;
    clearSelectionRef.current();
    setAllMatchingSelected(false);
  }, []);

  const activeSelectionQueryKey = pageCacheKey(1, searchQuery, {
    type: folderType,
    source: sourceFilter,
    sortKey,
    pageSize,
    viewMode,
    hasControlnet,
    recentDays,
  });
  const activeSelectionQueryKeyRef = React.useRef(activeSelectionQueryKey);
  activeSelectionQueryKeyRef.current = activeSelectionQueryKey;

  const handleSelectAllMatching = React.useCallback(async () => {
    if (selectingAll || totalItems <= 0) return;
    const operationVersion = ++selectionOperationVersionRef.current;
    const queryKey = activeSelectionQueryKeyRef.current;
    const effectiveSource = viewModeRef.current === 'uploads' ? 'uploaded' : sourceFilterRef.current;
    const baseQuery: MediaLibraryListQuery = {
      page: 1,
      limit: 100,
      search: searchQuery || undefined,
      type: folderTypeRef.current === 'all' ? undefined : folderTypeRef.current,
      source: effectiveSource === 'all' ? undefined : effectiveSource,
      favourite: viewModeRef.current === 'favourites' ? true : undefined,
      sort: sortKeyRef.current,
      created_after: createdAfterForRecentDays(recentDaysRef.current),
      has_controlnet: hasControlnetRef.current ? true : undefined,
    };
    setSelectingAll(true);
    setErrorMessage('');
    try {
      const first = await API.listMediaLibraryPaged(baseQuery);
      const collected = [...first.items];
      const matchingTotal = first.total ?? collected.length;
      const pageCount = Math.max(1, Math.ceil(matchingTotal / 100));
      const remaining = Array.from({ length: Math.max(0, pageCount - 1) }, (_, index) => index + 2);
      // Four concurrent page reads keep a large selection responsive without
      // flooding the Go/Store services with one request per page at once.
      for (let index = 0; index < remaining.length; index += 4) {
        const batch = remaining.slice(index, index + 4);
        const results = await Promise.all(
          batch.map((page) => API.listMediaLibraryPaged({ ...baseQuery, page }))
        );
        results.forEach((result) => collected.push(...result.items));
      }
      if (
        activeSelectionQueryKeyRef.current !== queryKey ||
        selectionOperationVersionRef.current !== operationVersion
      ) return;
      addManySelectionRef.current(collected);
      setAllMatchingSelected(true);
      setStatusMessage(`Selected all ${collected.length} matching media items.`);
    } catch (error) {
      setAllMatchingSelected(false);
      setErrorMessage(toActionableError(error, 'list'));
    } finally {
      setSelectingAll(false);
    }
  }, [searchQuery, selectingAll, totalItems]);

  // Invalidate cached pages after a list-mutating action so the next reopen
  // revalidates rather than skipping (alreadyCurrent) and painting stale rows.
  const invalidateCache = React.useCallback(() => {
    invalidateLibraryCache();
    lastLoadedKeyRef.current = null;
  }, []);

  // ── Bulk actions (manage mode) ─────────────────────────────────────────────
  const selectedItemsRef = React.useRef(selection.selectedItems);
  selectedItemsRef.current = selection.selectedItems;

  // Bulk favourite: toggle by the majority state of the selection (if most are
  // already favourites, the action un-favourites; otherwise it favourites all).
  const handleBulkFavourite = React.useCallback(async () => {
    const picked = selectedItemsRef.current;
    if (picked.length === 0) return;
    const favCount = picked.filter((it) => it.is_favourite).length;
    const next = favCount <= picked.length / 2; // favourite-all unless mostly favourited
    setErrorMessage('');
    setStatusMessage('');
    const ids = picked.map((it) => it.media_id || it.id);
    ids.forEach((id) => {
      inflightFavouritesRef.current.set(id, next);
      patchCachedFavourite(id, next);
    });
    setLibraryItems((prev) =>
      prev.map((item) =>
        ids.includes(item.media_id || item.id) ? { ...item, is_favourite: next } : item
      )
    );
    try {
      for (let index = 0; index < ids.length; index += 10) {
        await Promise.all(ids.slice(index, index + 10).map((id) => API.toggleFavourite(id, next)));
      }
      setStatusMessage(`${next ? 'Favourited' : 'Unfavourited'} ${ids.length} file${ids.length === 1 ? '' : 's'}.`);
    } catch (error) {
      setErrorMessage(toActionableError(error, 'metadata'));
    } finally {
      ids.forEach((id) => {
        if (inflightFavouritesRef.current.get(id) === next) {
          inflightFavouritesRef.current.delete(id);
        }
      });
    }
  }, []);

  // Bulk delete: remove every selected item, optimistically, then clear.
  const handleBulkDelete = React.useCallback(async () => {
    const picked = selectedItemsRef.current;
    if (picked.length === 0) return;
    const ids = picked.map((it) => it.media_id || it.id);
    setDeleting(true);
    setErrorMessage('');
    setStatusMessage('');
    let removed = 0;
    for (const id of ids) {
      try {
        await API.deleteMediaItem(id);
        removed += 1;
        removeSelectionRef.current(id);
      } catch (error) {
        setErrorMessage(toActionableError(error, 'remove'));
      }
    }
    if (removed > 0) {
      setLibraryItems((prev) => prev.filter((item) => !ids.includes(item.media_id || item.id)));
      setDismissedMediaIds((prev) => [...prev, ...ids.filter((id) => !prev.includes(id))]);
      invalidateCache();
      setStatusMessage(`Removed ${removed} file${removed === 1 ? '' : 's'}.`);
    }
    setDeleting(false);
  }, [invalidateCache]);

  const handleUpload = async () => {
    if (!uploadFiles.length) return;
    setUploading(true);
    setErrorMessage('');
    setStatusMessage('');
    let uploadedCount = 0;

    // Every uploaded image is described for the AI reviewer by default — one
    // small vision call each. On a bulk drop most of those files will never be
    // referenced by a pipeline, so the whole batch opts out and the user runs
    // Describe on the few that matter (UploadTab says so before they hit
    // Upload).
    const describe = uploadFiles.length > BULK_DESCRIBE_THRESHOLD ? false : undefined;

    for (const file of uploadFiles) {
      try {
        await API.uploadMediaLibraryFile(file, {
          type: inferUploadType(file),
          source: 'manual_upload',
          describe,
        });
        uploadedCount += 1;
      } catch (error) {
        setErrorMessage(toActionableError(error, 'upload'));
      }
    }

    setUploading(false);
    setUploadFiles([]);
    if (uploadedCount > 0) {
      setStatusMessage(`Uploaded ${uploadedCount} file${uploadedCount === 1 ? '' : 's'} successfully.`);
      setActiveTab('browse');
      invalidateCache();
      await loadLibrary(1, searchQuery);
    }
  };

  const handleSaveContext = async () => {
    if (!activeItem || mode === 'select') return;
    const mediaId = (activeItem.media_id || activeItem.id || '').trim();
    if (!mediaId) return;

    setSavingContext(true);
    setErrorMessage('');
    setStatusMessage('');
    try {
      await API.editMediaMetadata(mediaId, 'user_context', contextDraft.trim());
      setLibraryItems((previous) => {
        const nextMetadata = { ...(activeItem.metadata || {}), user_context: contextDraft.trim() };
        const matchIndex = previous.findIndex((item) => (item.media_id || item.id) === mediaId);
        if (matchIndex >= 0) {
          return previous.map((item, index) =>
            index === matchIndex ? { ...item, metadata: nextMetadata } : item
          );
        }
        return [{ ...activeItem, metadata: nextMetadata }, ...previous];
      });
      invalidateCache();
      setStatusMessage('Saved file context.');
    } catch (error) {
      setErrorMessage(toActionableError(error, 'metadata'));
    } finally {
      setSavingContext(false);
    }
  };

  const handleDescribe = async (force: boolean) => {
    if (!activeItem || mode === 'select') return;
    const mediaId = (activeItem.media_id || activeItem.id || '').trim();
    if (!mediaId) return;
    // Capture the item this request is FOR. The user may navigate to a
    // different item (which reseeds descriptionDraft) before the response
    // lands — the metadata merge below is keyed on `mediaId` so it always
    // lands on the right item, but the draft/status are only about whatever
    // item is on screen *now*, so those are gated on activeMediaIdRef.
    const targetMediaId = mediaId;

    setDescribing(true);
    setErrorMessage('');
    setStatusMessage('');
    try {
      const result = await MediaAPI.describeMedia(mediaId, force);
      setLibraryItems((previous) => {
        // ai_description_at mirrors the timestamp the backend just stamped on
        // the row. Without it here, this item's local metadata would lack the
        // key entirely, and handleSaveDescription's later replaceMediaMetadata
        // — a REPLACE, not a merge — would drop it from the server on the next
        // manual save.
        const nextMetadata = {
          ...(activeItem.metadata || {}),
          ai_description: result.description,
          ai_description_model: result.model,
          ai_description_at: new Date().toISOString(),
          ai_description_user_edited: false,
        };
        const matchIndex = previous.findIndex((item) => (item.media_id || item.id) === mediaId);
        if (matchIndex >= 0) {
          return previous.map((item, index) =>
            index === matchIndex ? { ...item, metadata: nextMetadata } : item
          );
        }
        return [{ ...activeItem, metadata: nextMetadata }, ...previous];
      });
      invalidateCache();
      if (activeMediaIdRef.current === targetMediaId) {
        setDescriptionDraft(result.description);
        setStatusMessage(result.cached ? 'Description already stored.' : 'Described.');
      }
    } catch (error) {
      setErrorMessage(toActionableError(error, 'metadata'));
    } finally {
      setDescribing(false);
    }
  };

  const handleSaveDescription = async () => {
    if (!activeItem || mode === 'select') return;
    const mediaId = (activeItem.media_id || activeItem.id || '').trim();
    if (!mediaId) return;

    setSavingDescription(true);
    setErrorMessage('');
    setStatusMessage('');
    try {
      const trimmed = descriptionDraft.trim();
      // One call for both keys — replaceMediaMetadata takes the full metadata
      // object (client-merged, matching this codebase's established pattern
      // for it elsewhere, e.g. MediaEditorSection's `{ ...item.metadata, order }`)
      // so ai_description and ai_description_user_edited are written together.
      // A two-call (editMediaMetadata x2) version could store the text but
      // fail to flag it as user-edited, attributing user text to the model.
      const nextMetadata = {
        ...(activeItem.metadata || {}),
        ai_description: trimmed,
        ai_description_user_edited: true,
      };
      await API.replaceMediaMetadata(mediaId, nextMetadata);
      setLibraryItems((previous) => {
        const matchIndex = previous.findIndex((item) => (item.media_id || item.id) === mediaId);
        if (matchIndex >= 0) {
          return previous.map((item, index) =>
            index === matchIndex ? { ...item, metadata: nextMetadata } : item
          );
        }
        return [{ ...activeItem, metadata: nextMetadata }, ...previous];
      });
      invalidateCache();
      setStatusMessage('Saved description.');
    } catch (error) {
      setErrorMessage(toActionableError(error, 'metadata'));
    } finally {
      setSavingDescription(false);
    }
  };

  const handleRenameActive = async () => {
    if (!activeItem || mode === 'select') return;
    const mediaId = (activeItem.media_id || activeItem.id || '').trim();
    const nextName = renameDraft.trim();
    if (!mediaId || !nextName) {
      setErrorMessage('Enter a file name before renaming.');
      return;
    }

    setRenaming(true);
    setErrorMessage('');
    setStatusMessage('');
    try {
      const renamed = await API.renameMediaLibraryItem(mediaId, nextName);
      const nextMediaId = renamed.media_id || renamed.id || mediaId;
      setLibraryItems((previous) => {
        const nextItem = {
          ...activeItem,
          ...renamed,
          media_id: nextMediaId,
          id: nextMediaId,
          name: renamed.name || nextName,
        };
        const matchIndex = previous.findIndex((item) => (item.media_id || item.id) === mediaId);
        if (matchIndex >= 0) {
          return previous.map((item, index) => (index === matchIndex ? nextItem : item));
        }
        return [nextItem, ...previous];
      });
      setActiveMediaId(nextMediaId);
      setRenameDraft(renamed.name || nextName);
      invalidateCache();
      setStatusMessage('Renamed file successfully.');
    } catch (error) {
      setErrorMessage(toActionableError(error, 'rename'));
    } finally {
      setRenaming(false);
    }
  };

  const handleRemoveActive = async () => {
    if (!activeItem || mode === 'select') return;
    const mediaId = (activeItem.media_id || activeItem.id || '').trim();
    if (!mediaId) return;

    setDeleting(true);
    setErrorMessage('');
    setStatusMessage('');
    try {
      await API.deleteMediaItem(mediaId);
      setLibraryItems((previous) => previous.filter((item) => (item.media_id || item.id) !== mediaId));
      setDismissedMediaIds((previous) =>
        previous.includes(mediaId) ? previous : [...previous, mediaId]
      );
      removeSelectionRef.current(mediaId);
      setActiveMediaId('');
      setContextDraft('');
      setDescriptionDraft('');
      setRenameDraft('');
      invalidateCache();
      setStatusMessage('Removed file from library.');
    } catch (error) {
      setErrorMessage(toActionableError(error, 'remove'));
    } finally {
      setDeleting(false);
    }
  };

  const handleCropSaved = React.useCallback(async (cropped: MediaLibraryItem) => {
    invalidateCache();
    setErrorMessage('');
    setStatusMessage(`Saved cropped copy “${cropped.name || 'cropped image'}”. The original was kept.`);
    await loadLibrary(currentPage, searchQuery, undefined, true);
  }, [currentPage, invalidateCache, loadLibrary, searchQuery]);

  const handleDatasetSaved = React.useCallback((datasetName: string, action: 'created' | 'added') => {
    clearSelectionRef.current();
    setAllMatchingSelected(false);
    setErrorMessage('');
    setStatusMessage(action === 'created'
      ? `Created dataset “${datasetName}” from the selected images.`
      : `Added the selected images to dataset “${datasetName}”.`);
  }, []);

  return {
    activeItem,
    activeTab,
    allItems: items,
    fullItems,
    contextDraft,
    aiDescription,
    descriptionDraft,
    renameDraft,
    errorMessage,
    filteredItems,
    folderCounts,
    folderType,
    loading,
    savingContext,
    describing,
    savingDescription,
    renaming,
    deleting,
    searchQuery,
    selectedItems,
    selectedIds,
    sourceFilter,
    sortKey,
    pageSize,
    hasControlnet,
    recentDays,
    activePresetId,
    presets: [...BUILT_IN_MEDIA_PRESETS, ...savedPresets],
    statusMessage,
    uploadFiles,
    uploading,
    currentPage,
    totalPages,
    totalItems,
    folderTotals,
    apiStats,
    viewMode,
    selectingAll,
    allMatchingSelected,
    togglingFavouriteId,
    setActiveTab,
    setContextDraft,
    setDescriptionDraft,
    setRenameDraft,
    // Expose the filter-aware wrappers under the existing setter names so every
    // consumer (page + modal) triggers a server reload on a type/source change
    // without any change at the call site. The raw React setters are still used
    // internally (e.g. the open effect resets to 'all' without a reload).
    setFolderType: handleFolderTypeChange,
    setSearchQuery,
    setSourceFilter: handleSourceFilterChange,
    setUploadFiles,
    setViewMode: handleViewModeChange,
    handleFileClick,
    handleSaveContext,
    handleDescribe,
    handleSaveDescription,
    handleRenameActive,
    handleRemoveActive,
    handleUpload,
    handleSearchSubmit,
    handlePageChange,
    handlePageSizeChange,
    handleSortChange,
    handleHasControlnetChange,
    handleRecentDaysChange,
    handleApplyPreset,
    handleSavePreset,
    handleDeletePreset,
    handleClearFilters,
    handleSelectAllMatching,
    handleToggleFavourite,
    handleToggleSelect,
    handleRangeSelect,
    handleInspect,
    handleClearSelection,
    handleBulkFavourite,
    handleBulkDelete,
    handleCropSaved,
    handleDatasetSaved,
    selectedImageCount: selection.selectedItems.filter((item) => item.type === 'image').length,
    selectedCount: selection.count,
    loadLibrary,
  };
};
